import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { STATES } from "@/lib/constants";
import { LOCAL_TEXT } from "@/lib/landing_text";

const money = (n) => (n >= 999500 ? `$${(n / 1e6).toFixed(2)}M` : `$${Math.round(n / 1000)}K`);

function WinnerCard({ w, T }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 min-h-[210px] fade-up" key={w.company} data-testid="local-winner-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display text-xl font-bold text-slate-900 truncate" data-testid="local-winner-company">{w.company}</div>
          <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" />{w.city ? `${w.city}, ` : ""}{w.state}</div>
        </div>
        <div className="text-right shrink-0"><div className="font-display text-2xl font-extrabold text-emerald-700" data-testid="local-winner-amount">{money(w.amount)}</div><div className="text-[11px] font-mono uppercase text-slate-400">{w.program} {T.phase(w.phase)} · {w.year}</div></div>
      </div>
      <p className="text-sm text-slate-700 mt-4 leading-relaxed line-clamp-2">{w.title}</p>
      <div className="mt-4 flex flex-wrap gap-1.5 text-[11px]">
        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{w.agency}</span>
        {w.employees != null && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{T.employees(w.employees)}</span>}
        {w.women_owned && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{T.women}</span>}
        {w.hubzone && <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">{T.hub}</span>}
      </div>
    </div>
  );
}

export function LocalWinners({ lang }) {
  const T = LOCAL_TEXT[lang];
  const [state, setState] = useState("all");
  const [rows, setRows] = useState(null);
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    setRows(null); setI(0);
    api.get("/public/sbir-winners", { params: state === "all" ? {} : { state } }).then((r) => setRows(r.data.winners)).catch(() => setRows([]));
  }, [state]);
  useEffect(() => {
    if (paused || !rows || rows.length < 2) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % rows.length), 5000);
    return () => clearInterval(t);
  }, [paused, rows]);
  const step = (d) => setI((x) => (x + d + rows.length) % rows.length);
  return (
    <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start" data-testid="local-winners" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="lg:col-span-4">
        <h3 className="font-display text-2xl font-bold text-slate-900">{T.title}</h3>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">{T.sub}</p>
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className="mt-4 w-64" data-testid="local-winners-state-select"><SelectValue placeholder={T.pick} /></SelectTrigger>
          <SelectContent className="max-h-72"><SelectItem value="all">{T.all}</SelectItem>{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="lg:col-span-8 min-w-0">
        {!rows ? <div className="h-[210px] rounded-lg bg-slate-100 animate-pulse" /> : rows.length === 0 ? <p className="text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg p-8" data-testid="local-winners-empty">{T.empty}</p> : (
          <>
            <WinnerCard w={rows[i]} T={T} key={`${state}-${i}`} />
            <div className="mt-3 flex items-center gap-3">
              <button onClick={() => step(-1)} aria-label={T.prev} className="h-8 w-8 rounded-full border border-slate-300 grid place-items-center hover:bg-slate-100" data-testid="local-winners-prev"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => step(1)} aria-label={T.next} className="h-8 w-8 rounded-full border border-slate-300 grid place-items-center hover:bg-slate-100" data-testid="local-winners-next"><ChevronRight className="h-4 w-4" /></button>
              <div className="flex gap-1">{rows.map((_, k) => <span key={k} className={`h-1.5 rounded-full transition-all ${k === i ? "w-5 bg-emerald-600" : "w-1.5 bg-slate-300"}`} />)}</div>
              <span className="ml-auto text-xs text-slate-400 font-mono" data-testid="local-winners-position">{i + 1} / {rows.length}</span>
            </div>
          </>
        )}
        <p className="mt-3 text-[11px] text-slate-400">{T.source}</p>
      </div>
    </div>
  );
}
